import React from 'react'
import useSWRInfinite from 'swr/infinite'

import BodyWidget, { BodyWidgetProps } from '../../atoms/BodyWidget'
import LoadingSpinner from '../../atoms/LoadingSpinner'
import Infinite from '../../organisms/ChatWindow/Timeline/Infinite'

import { generateRecurrentEventsByDateRange } from '@SonarCX/pages/Dashboard/PlannerTab/helpers/remindersHelper'
import { useAppDispatch, useAppSelector } from '@SonarCX/hooks/redux'
import { changeMutateDashboard, selectReminder } from '@SonarCX/slices/reminder'

import useStyles from './styles'

export type APITaskData =
  | WithTokenPagination<APIReminder[]>
  | WithIndexPagination<APIReminder[]>

type ReminderWidgetProps = Omit<BodyWidgetProps, 'title' | 'children'> & {
  title: string
  getKey: (pageIndex: number, previousPageData: APITaskData) => string
  format?: (task: APIReminder[]) => (APIReminder & Record<string, any>)[]
  rowRenderer: (props: {
    reminders: (APIReminder & Record<string, any>)[]
    apiReminders: APIReminder[]
  }) => React.ReactNode[]
  noRemindersText?: string
  dateRangeEnd: string
  dateRangeStart: string
}

export const PAGE_SIZE = 25

const ReminderWidget: React.FC<ReminderWidgetProps> = ({
  title,
  getKey,
  format,
  rowRenderer,
  noRemindersText = 'No reminders',
  dateRangeEnd,
  dateRangeStart,
  ...props
}) => {
  const dispatch = useAppDispatch()
  const { mutateDashboard } = useAppSelector(selectReminder)
  const { data, size, setSize, mutate } = useSWRInfinite<APIReminder>(getKey)
  const containerRef = React.useRef()

  const reminders = React.useMemo(
    () => (data ? [].concat(...data) : []),
    [data]
  )
  const isLoadingInitialData = React.useMemo(() => !data, [data])
  const isLoadingMore = React.useMemo(
    () =>
      isLoadingInitialData ||
      (size > 0 && data && typeof data[size - 1] === 'undefined'),
    [isLoadingInitialData, size, data]
  )
  const after = React.useMemo(() => {
    if (reminders.length === 0) {
      return
    }
    const { next, page, per_page, total } =
      reminders[reminders.length - 1]?.pagination
    if (next || next === null) {
      return next as string
    } else {
      if (page + 1 < Math.ceil(total / per_page)) {
        return (page + 1) as number
      }
      return null
    }
  }, [reminders])
  const flatReminders = React.useMemo<APIReminder[]>(
    () => reminders.flatMap(x => x.data),
    [reminders]
  )
  const flatRecurrentReminders = React.useMemo(
    () =>
      generateRecurrentEventsByDateRange(
        flatReminders,
        dateRangeStart,
        dateRangeEnd
      ),
    [dateRangeEnd, dateRangeStart, flatReminders]
  )

  const empty = React.useMemo(
    () => isLoadingInitialData && flatRecurrentReminders.length === 0,
    [isLoadingInitialData, flatRecurrentReminders]
  )
  const formatted = React.useMemo(() => {
    if (!format) {
      return flatRecurrentReminders
    }

    return format(flatRecurrentReminders)
  }, [format, flatRecurrentReminders])

  const formattedAndSorted = React.useMemo(
    //@ts-expect-error we are sorting dates here
    () => formatted.sort((a, b) => new Date(a.stamp) - new Date(b.stamp)),
    [formatted]
  )

  React.useEffect(() => {
    if (mutateDashboard) {
      mutate()
      dispatch(changeMutateDashboard(false))
    }
  }, [dispatch, mutate, mutateDashboard])

  const classes = useStyles({ empty })

  const emptyDiv = <div className={classes.emptyTitle}>{noRemindersText}</div>

  // Not ideal but needed here, this function/component has to return an array to work with Infinite
  const children = rowRenderer({
    reminders: formattedAndSorted,
    apiReminders: flatReminders
  })

  return (
    <BodyWidget title={title} {...props}>
      <div ref={containerRef} className={classes.root}>
        {isLoadingInitialData ? (
          <LoadingSpinner className={classes.loading} />
        ) : empty ? (
          emptyDiv
        ) : (
          // @ts-ignore
          <Infinite
            containerRef={containerRef}
            PAGE_SIZE={PAGE_SIZE}
            loadAtTop={false}
            TRIGGER_HEIGHT={50}
            setSize={setSize}
            size={size}
            isLoadingMore={isLoadingMore}
            after={after}
            isSWR
          >
            {children}
          </Infinite>
        )}

        {!isLoadingInitialData && !Boolean(formatted.length) && emptyDiv}
      </div>

      {!isLoadingInitialData && !empty && <div className={classes.bottomPad} />}
    </BodyWidget>
  )
}

export default ReminderWidget
